import React from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { PRIORITY_OPTIONS } from '../lib/priority';

/**
 * Reusable "Follow-up Required?" section for maintenance-log forms.
 *
 * Props:
 *   value: { enabled, description, priority, due_date, kind }
 *   onChange: (nextValue) => void
 *
 * When `enabled` is true the user picks:
 *   • kind = "minor"       → tracked on the Dashboard, closed via a new log.
 *   • kind = "work_order"  → auto-spawns a linked Work Order, closed by
 *                             completing that WO.
 *
 * Parent components read `value.enabled` and (only when true) send the rest
 * to the backend as `follow_up: { description, priority, due_date, kind }`.
 */
export const emptyFollowUp = () => ({
  enabled: false,
  description: '',
  priority: 'p1',
  due_date: '',
  kind: 'minor',
});

const FollowUpFields = ({ value, onChange, testIdPrefix = 'follow-up' }) => {
  const v = value || emptyFollowUp();

  const set = (patch) => onChange({ ...v, ...patch });

  return (
    <div className="border border-amber-200 bg-amber-50/50 rounded-md p-3 space-y-3">
      <label className="inline-flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          className="h-4 w-4"
          checked={!!v.enabled}
          onChange={(e) => set({ enabled: e.target.checked })}
          data-testid={`${testIdPrefix}-toggle`}
        />
        <span className="text-sm font-medium text-slate-800">
          Follow-up Action Required?
        </span>
      </label>

      {v.enabled && (
        <div className="space-y-3 pl-6">
          <div className="space-y-1">
            <Label htmlFor={`${testIdPrefix}-description`}>Description *</Label>
            <Textarea
              id={`${testIdPrefix}-description`}
              data-testid={`${testIdPrefix}-description`}
              rows={2}
              value={v.description}
              onChange={(e) => set({ description: e.target.value })}
              placeholder='e.g., "Recheck torque in 2 days" or "Replace seal kit — schedule shutdown"'
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor={`${testIdPrefix}-priority`}>Priority</Label>
              <select
                id={`${testIdPrefix}-priority`}
                data-testid={`${testIdPrefix}-priority`}
                value={v.priority}
                onChange={(e) => set({ priority: e.target.value })}
                className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring"
              >
                {PRIORITY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor={`${testIdPrefix}-due-date`}>Due Date</Label>
              <Input
                id={`${testIdPrefix}-due-date`}
                data-testid={`${testIdPrefix}-due-date`}
                type="date"
                value={v.due_date}
                onChange={(e) => set({ due_date: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label>Kind</Label>
            <div className="flex flex-col gap-2">
              <label className="inline-flex items-start gap-2 cursor-pointer p-2 rounded border border-slate-200 hover:bg-white">
                <input
                  type="radio"
                  name={`${testIdPrefix}-kind`}
                  className="mt-1"
                  checked={v.kind === 'minor'}
                  onChange={() => set({ kind: 'minor' })}
                  data-testid={`${testIdPrefix}-kind-minor`}
                />
                <span className="text-sm">
                  <span className="font-medium">Minor</span>
                  <span className="block text-xs text-slate-500">
                    Tracked on the Dashboard. Close it by logging the follow-up work.
                  </span>
                </span>
              </label>
              <label className="inline-flex items-start gap-2 cursor-pointer p-2 rounded border border-slate-200 hover:bg-white">
                <input
                  type="radio"
                  name={`${testIdPrefix}-kind`}
                  className="mt-1"
                  checked={v.kind === 'work_order'}
                  onChange={() => set({ kind: 'work_order' })}
                  data-testid={`${testIdPrefix}-kind-wo`}
                />
                <span className="text-sm">
                  <span className="font-medium">Significant — create a linked Work Order</span>
                  <span className="block text-xs text-slate-500">
                    Auto-spawns a Work Order pre-filled from this follow-up. Completing that
                    WO closes the follow-up automatically.
                  </span>
                </span>
              </label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FollowUpFields;
